import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../lib/AuthContext';
import './CompanySignUp.css';

const TIERS = [
  {
    id: 'base',
    label: 'Base',
    price: '$99/mo',
    amount: 99,
    description: 'PubSilo-hosted articles. Readers stay on PubSilo to read content.',
    features: ['Unlimited articles', 'Token-based purchases', 'Analytics dashboard'],
  },
  {
    id: 'elevated',
    label: 'Elevated',
    price: '$299/mo',
    amount: 299,
    description: 'Create articles on PubSilo, but readers redirect back to your site after purchasing.',
    features: ['Everything in Base', 'External redirect after purchase', 'Embed button generator'],
    highlight: true,
  },
  {
    id: 'grand',
    label: 'Grand',
    price: '$749/mo',
    amount: 749,
    description: 'Full plug-and-play. Use your own article slugs — no pre-registration on PubSilo required.',
    features: ['Everything in Elevated', 'Custom article slugs', 'API embed keys', 'Auto article creation'],
  },
];

function formatCardNumber(val) {
  return val.replace(/\D/g, '').slice(0, 16).replace(/(.{4})/g, '$1 ').trim();
}

function formatExpiry(val) {
  const digits = val.replace(/\D/g, '').slice(0, 4);
  if (digits.length >= 3) return digits.slice(0, 2) + '/' + digits.slice(2);
  return digits;
}

function CompanySignUp() {
  const navigate = useNavigate();
  const { setAccessToken } = useAuth();
  const [step, setStep] = useState('info'); // info | tier | payment
  const [formData, setFormData] = useState({
    username: '',
    company_name: '',
    account_type: 'standard',
    contact_info: { phone: '', email: '', address: '' },
    email: '',
    password: '',
    confirmPassword: '',
    mediaType: 'news',
  });
  const [addressFields, setAddressFields] = useState({ street: '', city: '', state: '', zip: '' });
  const [selectedTier, setSelectedTier] = useState('base');
  const [card, setCard] = useState({ name: '', number: '', expiry: '', cvv: '' });
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [pendingEmail, setPendingEmail] = useState(null);
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('contact_')) {
      const field = name.replace('contact_', '');
      setFormData(prev => ({ ...prev, contact_info: { ...prev.contact_info, [field]: value } }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setAddressFields(prev => ({ ...prev, [name]: value }));
  };

  const handleCardChange = (e) => {
    const { name, value } = e.target;
    let formatted = value;
    if (name === 'number') formatted = formatCardNumber(value);
    if (name === 'expiry') formatted = formatExpiry(value);
    if (name === 'cvv') formatted = value.replace(/\D/g, '').slice(0, 4);
    setCard(prev => ({ ...prev, [name]: formatted }));
    setFieldErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleInfoNext = (e) => {
    e.preventDefault();
    setError('');
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setStep('tier');
  };

  const handleTierNext = () => {
    setStep('payment');
  };

  const validateCard = () => {
    const errors = {};
    if (!card.name.trim()) errors.name = 'Name is required';
    const digits = card.number.replace(/\s/g, '');
    if (digits.length < 13) errors.number = 'Enter a valid card number';
    if (!/^\d{2}\/\d{2}$/.test(card.expiry)) errors.expiry = 'Enter expiry as MM/YY';
    if (card.cvv.length < 3) errors.cvv = 'Enter a valid CVV';
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const errors = validateCard();
    if (Object.keys(errors).length) { setFieldErrors(errors); return; }

    setLoading(true);
    try {
      const combinedAddress = [addressFields.street, addressFields.city, addressFields.state, addressFields.zip].filter(Boolean).join(', ');
      const { data } = await axios.post('/api/companies', {
        ...formData,
        contact_info: { ...formData.contact_info, address: combinedAddress },
        subscription_tier: selectedTier,
      });
      if (data.token) {
        setAccessToken(data.token);
        localStorage.setItem('hasSession', 'true');
        localStorage.setItem('userType', 'company');
        localStorage.setItem('username', data.user.username);
        localStorage.setItem('companyName', data.user.company_name);
        navigate(`/adminHUD/${encodeURIComponent(data.user.company_name)}`);
        return;
      }
      setPendingEmail(formData.email);
    } catch (err) {
      setError(err.response?.data?.error || 'Sign-up failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setResendMsg('');
    try {
      await axios.post('/api/auth/resend-verification', { email: pendingEmail });
      setResendMsg('Verification email resent! Check your inbox.');
    } catch (err) {
      setResendMsg(err.response?.data?.error || 'Could not resend. Try again in a moment.');
    } finally {
      setResending(false);
    }
  };

  const tierInfo = TIERS.find(t => t.id === selectedTier);

  if (pendingEmail) {
    return (
      <div style={{ minHeight: '100vh', background: '#f4f4f4', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Arial, sans-serif', padding: '2rem' }}>
        <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 4px 24px rgba(0,0,0,0.1)', padding: '3rem 2.5rem', maxWidth: '440px', width: '100%', textAlign: 'center' }}>
          <div style={{ fontSize: '52px', marginBottom: '1rem' }}>📧</div>
          <h2 style={{ color: '#0d3165', fontWeight: 800, marginBottom: '0.5rem' }}>Check your email</h2>
          <p style={{ color: '#555', fontSize: '0.95rem', lineHeight: 1.6, marginBottom: '1.5rem' }}>
            We sent a verification link to <strong>{pendingEmail}</strong>.<br />
            Click the link to activate your publisher account.
          </p>
          <p style={{ color: '#888', fontSize: '0.82rem', marginBottom: '1.5rem', lineHeight: 1.5 }}>
            The link expires in 24 hours. Check your spam folder if you don't see it.
          </p>
          {resendMsg ? (
            <div style={{ fontSize: '13px', color: resendMsg.startsWith('Verification') ? '#27ae60' : '#c0392b', marginBottom: '1rem' }}>{resendMsg}</div>
          ) : (
            <button
              onClick={handleResend}
              disabled={resending}
              style={{ background: 'transparent', border: '1px solid #ccc', borderRadius: '6px', padding: '0.6rem 1.5rem', fontSize: '0.85rem', color: '#555', cursor: resending ? 'not-allowed' : 'pointer', opacity: resending ? 0.6 : 1 }}
            >
              {resending ? 'Sending…' : 'Resend verification email'}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="company-signup-container">
      <div className="company-signup-form-wrapper" style={{ maxWidth: step === 'tier' ? 720 : 480 }}>
        <img src="/pubsilo.png" alt="PubSilo" className="signup-logo" />
        <h1>Join as Company</h1>

        {/* Step indicator */}
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', marginBottom: '1.5rem' }}>
          {['info', 'tier', 'payment'].map((s, i) => (
            <div key={s} style={{
              width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.8rem', fontWeight: 700,
              background: step === s ? '#0d3165' : ((['info','tier','payment'].indexOf(step) > i) ? '#4caf50' : '#ddd'),
              color: step === s || (['info','tier','payment'].indexOf(step) > i) ? '#fff' : '#666',
            }}>{i + 1}</div>
          ))}
        </div>

        {error && <div className="error-message">{error}</div>}

        {/* ── STEP 1: Account Info ── */}
        {step === 'info' && (
          <form onSubmit={handleInfoNext}>
            <p className="subtitle">Create your company account</p>
            <div className="form-group">
              <label htmlFor="username">Company Username</label>
              <input type="text" id="username" name="username" value={formData.username} onChange={handleChange} required placeholder="Username" />
            </div>
            <div className="form-group">
              <label htmlFor="company_name">Company Name</label>
              <input type="text" id="company_name" name="company_name" value={formData.company_name} onChange={handleChange} required placeholder="Your company name" />
            </div>
            <div className="form-group">
              <label htmlFor="mediaType">Media Type</label>
              <select id="mediaType" name="mediaType" value={formData.mediaType} onChange={handleChange} style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: 6, border: '1px solid #ccc', fontSize: '0.95rem', background: '#fff' }}>
                <option value="news">News</option>
                {/* Magazine, Blog, and Podcast options will be added when those media types launch */}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="email">Company Email</label>
              <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required placeholder="company@email.com" />
            </div>
            <div className="form-group">
              <label htmlFor="contact_phone">Contact Phone</label>
              <input type="tel" id="contact_phone" name="contact_phone" value={formData.contact_info.phone} onChange={handleChange} placeholder="(555) 123-4567" />
            </div>
            <div className="form-group">
              <label>Street Address</label>
              <input type="text" name="street" value={addressFields.street} onChange={handleAddressChange} placeholder="123 Main St, Suite 400" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div className="form-group">
                <label>City</label>
                <input type="text" name="city" value={addressFields.city} onChange={handleAddressChange} placeholder="New York" />
              </div>
              <div className="form-group">
                <label>ZIP Code</label>
                <input type="text" name="zip" value={addressFields.zip} onChange={handleAddressChange} placeholder="10001" inputMode="numeric" maxLength="10" />
              </div>
            </div>
            <div className="form-group">
              <label>State</label>
              <select name="state" value={addressFields.state} onChange={handleAddressChange} style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: 6, border: '1px solid #ccc', fontSize: '0.95rem', background: '#fff' }}>
                <option value="">— Select State —</option>
                {['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'].map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div style={{ position: 'relative' }}>
                <input type={showPw ? 'text' : 'password'} id="password" name="password" value={formData.password} onChange={handleChange} required placeholder="Enter password" style={{ paddingRight: '2.5rem', width: '100%' }} />
                <button type="button" onClick={() => setShowPw(v => !v)} aria-label={showPw ? 'Hide password' : 'Show password'} style={{ position: 'absolute', right: '0.65rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '0.2rem', color: '#999', display: 'flex', alignItems: 'center' }}>
                  {showPw
                    ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  }
                </button>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div style={{ position: 'relative' }}>
                <input type={showConfirmPw ? 'text' : 'password'} id="confirmPassword" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} required placeholder="Confirm password" style={{ paddingRight: '2.5rem', width: '100%' }} />
                <button type="button" onClick={() => setShowConfirmPw(v => !v)} aria-label={showConfirmPw ? 'Hide password' : 'Show password'} style={{ position: 'absolute', right: '0.65rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '0.2rem', color: '#999', display: 'flex', alignItems: 'center' }}>
                  {showConfirmPw
                    ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  }
                </button>
              </div>
            </div>
            <button type="submit" className="btn-submit">Next: Choose Plan →</button>
          </form>
        )}

        {/* ── STEP 2: Tier Selection ── */}
        {step === 'tier' && (
          <div>
            <p className="subtitle">Choose the plan that fits your publishing needs</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
              {TIERS.map(tier => (
                <div
                  key={tier.id}
                  onClick={() => setSelectedTier(tier.id)}
                  style={{
                    border: selectedTier === tier.id ? '2.5px solid #0d3165' : '1.5px solid #ddd',
                    borderRadius: 10,
                    padding: '1.25rem 1rem',
                    cursor: 'pointer',
                    background: selectedTier === tier.id ? '#f0f6ff' : '#fff',
                    position: 'relative',
                    transition: 'all 0.15s',
                  }}
                >
                  {tier.highlight && (
                    <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: '#0d3165', color: '#fff', fontSize: '0.7rem', fontWeight: 700, padding: '2px 10px', borderRadius: 20, whiteSpace: 'nowrap' }}>
                      Most Popular
                    </div>
                  )}
                  <div style={{ fontWeight: 800, fontSize: '1.1rem', color: '#0d3165', marginBottom: '0.25rem' }}>{tier.label}</div>
                  <div style={{ fontWeight: 700, fontSize: '1.3rem', color: '#333', marginBottom: '0.5rem' }}>{tier.price}</div>
                  <div style={{ fontSize: '0.8rem', color: '#555', marginBottom: '0.75rem', lineHeight: 1.4 }}>{tier.description}</div>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {tier.features.map(f => (
                      <li key={f} style={{ fontSize: '0.78rem', color: '#444', marginBottom: '0.25rem' }}>✓ {f}</li>
                    ))}
                  </ul>
                  {selectedTier === tier.id && (
                    <div style={{ marginTop: '0.75rem', fontSize: '0.78rem', fontWeight: 700, color: '#0d3165' }}>✔ Selected</div>
                  )}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={() => setStep('info')} className="btn-submit" style={{ background: '#aaa', flex: 1 }}>← Back</button>
              <button onClick={handleTierNext} className="btn-submit" style={{ flex: 2 }}>Next: Payment →</button>
            </div>
          </div>
        )}

        {/* ── STEP 3: Payment ── */}
        {step === 'payment' && (
          <form onSubmit={handleSubmit}>
            <p className="subtitle">Complete your subscription</p>
            <div style={{ background: '#f0f6ff', border: '1px solid #c0d4f0', borderRadius: 8, padding: '0.85rem 1rem', marginBottom: '1.25rem', fontSize: '0.88rem' }}>
              <strong>{tierInfo?.label} Plan</strong> — {tierInfo?.price}
              <div style={{ color: '#555', marginTop: '0.2rem' }}>{tierInfo?.description}</div>
            </div>

            <div className="form-group">
              <label>Name on Card</label>
              <input type="text" name="name" value={card.name} onChange={handleCardChange} placeholder="Jane Smith" autoComplete="cc-name" />
              {fieldErrors.name && <span style={{ color: 'red', fontSize: '0.8rem' }}>{fieldErrors.name}</span>}
            </div>
            <div className="form-group">
              <label>Card Number</label>
              <input type="text" name="number" value={card.number} onChange={handleCardChange} placeholder="1234 5678 9012 3456" autoComplete="cc-number" inputMode="numeric" />
              {fieldErrors.number && <span style={{ color: 'red', fontSize: '0.8rem' }}>{fieldErrors.number}</span>}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div className="form-group">
                <label>Expiry</label>
                <input type="text" name="expiry" value={card.expiry} onChange={handleCardChange} placeholder="MM/YY" autoComplete="cc-exp" inputMode="numeric" />
                {fieldErrors.expiry && <span style={{ color: 'red', fontSize: '0.8rem' }}>{fieldErrors.expiry}</span>}
              </div>
              <div className="form-group">
                <label>CVV</label>
                <input type="text" name="cvv" value={card.cvv} onChange={handleCardChange} placeholder="123" autoComplete="cc-csc" inputMode="numeric" />
                {fieldErrors.cvv && <span style={{ color: 'red', fontSize: '0.8rem' }}>{fieldErrors.cvv}</span>}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
              <button type="button" onClick={() => setStep('tier')} className="btn-submit" style={{ background: '#aaa', flex: 1 }}>← Back</button>
              <button type="submit" className="btn-submit" disabled={loading} style={{ flex: 2 }}>
                {loading ? 'Processing...' : `Subscribe · ${tierInfo?.price}`}
              </button>
            </div>
          </form>
        )}

        <p className="login-prompt" style={{ marginTop: '1.5rem' }}>
          Already have an account? <Link to="/login">Log in</Link>
        </p>
      </div>
    </div>
  );
}

export default CompanySignUp;
